// Mantine
import { Button, Modal, NumberInput, Select, TextInput } from '@mantine/core'

// React
import { useState } from 'react'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function RetailerSettings({ retailer, reloadRetailer }) {
    // State
    const [markupRate, setMarkupRate] = useState(
        retailer.default_markup_rate * 100
    )
    const [publicId, setPublicId] = useState(retailer.public_id)
    const [pinRestricted, setPinRestricted] = useState(
        retailer.pin_restricted || ''
    )
    const [pinAdmin, setPinAdmin] = useState(retailer.pin_admin || '')
    const [hasFreeShipping, setHasFreeShipping] = useState(
        retailer.has_free_shipping ? 'free' : 'paid'
    )

    // Generate random 6-digit PIN
    const generateRandomPin = () => {
        return Math.floor(100000 + Math.random() * 900000).toString()
    }

    // Modal
    const [opened, setOpened] = useState(false)

    const openModal = () => {
        setOpened(true)
        setMarkupRate(retailer.default_markup_rate * 100)
        setPublicId(retailer.public_id)
        setPinRestricted(retailer.pin_restricted || '')
        setPinAdmin(retailer.pin_admin || '')
        setHasFreeShipping(retailer.has_free_shipping ? 'free' : 'paid')
    }

    const closeModal = () => {
        setOpened(false)
    }

    // Save settings
    const [isSaving, setIsSaving] = useState(false)
    const { requestPatch } = useAxiosApi()

    const saveSettings = async () => {
        setIsSaving(true)

        try {
            await requestPatch(`/retailers/${retailer.id}`, {
                default_markup_rate: markupRate / 100,
                public_id: publicId,
                pin_restricted: pinRestricted || null,
                pin_admin: pinAdmin || null,
                has_free_shipping: hasFreeShipping === 'free',
            })

            await reloadRetailer()

            closeModal()
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <>
            <div className="flex flex-col flex-1">
                <span className="font-medium">Paramètres</span>

                <span className="text-gray-500 text-sm">
                    Taux de marque : {retailer.default_markup_rate * 100 + '%'}
                </span>

                <span className="text-gray-500 text-sm">
                    Frais de port :{' '}
                    {retailer.has_free_shipping ? 'Offerts' : 'Payants'}
                </span>

                <span className="font-medium text-sm mt-3">
                    Accès espace revendeur
                </span>

                <span className="text-gray-500 text-sm">
                    Identifiant : {retailer.public_id ?? 'Non défini'}
                </span>

                <span className="text-gray-500 text-sm">
                    PIN Restreint : {retailer.pin_restricted ?? 'Non défini'}
                </span>

                <span className="text-gray-500 text-sm">
                    PIN Admin : {retailer.pin_admin ?? 'Non défini'}
                </span>

                <Button
                    onClick={openModal}
                    className="mt-5 mr-auto"
                    variant="default"
                >
                    Modifier les paramètres
                </Button>
            </div>

            <Modal
                opened={opened}
                onClose={closeModal}
                title="Modifier le revendeur"
            >
                <div className="flex flex-col gap-3">
                    <NumberInput
                        value={markupRate}
                        onChange={setMarkupRate}
                        label="Taux de marque"
                        allowDecimal={false}
                        min={0}
                        max={100}
                        step={1}
                        clampBehavior="strict"
                        suffix="%"
                    />

                    <Select
                        value={hasFreeShipping}
                        onChange={setHasFreeShipping}
                        label="Frais de port"
                        data={[
                            { value: 'paid', label: 'Payants' },
                            { value: 'free', label: 'Offerts' },
                        ]}
                    />

                    <span className="font-medium text-sm mt-2">
                        Accès espace revendeur
                    </span>

                    <TextInput
                        value={publicId}
                        onChange={(e) => {
                            const value = e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z]/g, '')
                            setPublicId(value)
                        }}
                        label="Identifiant"
                        placeholder="Identifiant public"
                        description="Lettres majuscules uniquement (A-Z)"
                    />

                    <div className="flex gap-2 items-end">
                        <TextInput
                            className="flex-1"
                            value={pinRestricted}
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, 6)
                                setPinRestricted(value)
                            }}
                            label="PIN Restreint"
                            placeholder="Code PIN restreint"
                            description="6 chiffres"
                            maxLength={6}
                            error={
                                pinRestricted && pinRestricted.length !== 6
                                    ? 'Le PIN doit contenir exactement 6 chiffres'
                                    : null
                            }
                        />
                        <Button
                            variant="light"
                            onClick={() =>
                                setPinRestricted(generateRandomPin())
                            }
                        >
                            Générer
                        </Button>
                    </div>

                    <div className="flex gap-2 items-end">
                        <TextInput
                            className="flex-1"
                            value={pinAdmin}
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, 6)
                                setPinAdmin(value)
                            }}
                            label="PIN Admin"
                            placeholder="Code PIN admin"
                            description="6 chiffres"
                            maxLength={6}
                            error={
                                pinAdmin && pinAdmin.length !== 6
                                    ? 'Le PIN doit contenir exactement 6 chiffres'
                                    : null
                            }
                        />
                        <Button
                            variant="light"
                            onClick={() => setPinAdmin(generateRandomPin())}
                        >
                            Générer
                        </Button>
                    </div>

                    <Button
                        className="mt-2 mx-auto"
                        onClick={saveSettings}
                        loading={isSaving}
                        disabled={
                            (pinRestricted && pinRestricted.length !== 6) ||
                            (pinAdmin && pinAdmin.length !== 6)
                        }
                    >
                        Sauvegarder
                    </Button>
                </div>
            </Modal>
        </>
    )
}
